/** @file clipFrame: a clip time or the host's frame resolved to the clip frame, wraps, elapsed and ticks. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { ClipTime } from '@zilo/pose3d-pose/time/timeTypes';

/** Rounding guard so `8 / 30 * 30` counts as frame 8 (only when the frame is derived from the time). */
const EPS = 1e-6;
/** The base clock of the clip time (18.11). */
const BASE_HZ = 60;

/**
 * Decision 4: `raw = start + floor(t * fps)`, `ticks = floor(t * 60)` (t clamped to >= 0). With `hostFrame`
 * (`ClipState.frame`, 21.2) the host's `floor(T * fps)` (start offset included, no epsilon) is `raw` as given, and
 * `hostLoops` (`ClipState.loops`) is the least wrap count, so an already wrapped host frame keeps its loop pass. A loop
 * (`frames - loopStart > 0`) plays `raw` until `frames - 1` and then continues from `loopStart`; a one-shot (or a loop of
 * length 0) shows frame `frames` and holds it (17.6).
 */
export function clipFrame(
  clip: Pick<Clip, 'fps' | 'frames' | 'start' | 'loopStart' | 'loop'>, timeSec: number, out: ClipTime, hostFrame?: number, hostLoops = 0,
): ClipTime {
  const t = Math.max(0, timeSec);
  const host = hostFrame !== undefined;
  const raw = host ? Math.max(0, Math.floor(hostFrame)) : clip.start + Math.floor(t * clip.fps + EPS);
  const len = clip.frames - clip.loopStart;
  const looping = clip.loop && len > 0;
  const least = host && looping ? Math.max(0, hostLoops) : 0;
  out.elapsed = Math.max(0, raw - clip.start);
  out.ticks = Math.floor(t * BASE_HZ + EPS);
  if (looping && raw >= clip.frames) {
    const k = raw - clip.loopStart;
    out.frame = clip.loopStart + (k % len);
    out.wraps = Math.max(Math.floor(k / len), least);
  } else {
    out.frame = looping ? raw : Math.min(raw, clip.frames);
    out.wraps = least;
  }
  return out;
}
